import { format } from 'date-fns';
import { es } from 'date-fns/locale';

import { AdapterDateFns }
  from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker }
  from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider }
  from '@mui/x-date-pickers/LocalizationProvider';


type Props = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  size?: 'small' | 'medium';
  minDate?: string | null;
  maxDate?: string | null;
};


function parseDateOnlyValue(
  value: string | null | undefined,
): Date | null {
  if (!value) {
    return null;
  }

  const match =
    /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);

  if (!match) {
    return null;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  const parsedDate = new Date(
    year,
    month - 1,
    day,
  );

  if (
    parsedDate.getFullYear() !== year
    || parsedDate.getMonth() !== month - 1
    || parsedDate.getDate() !== day
  ) {
    return null;
  }

  return parsedDate;
}


export default function ProaDatePicker({
  label,
  value,
  onChange,
  helperText,
  required = false,
  disabled = false,
  size = 'small',
  minDate,
  maxDate,
}: Props) {
  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={es}
    >
      <DatePicker
        label={label}
        value={parseDateOnlyValue(value)}
        disabled={disabled}
        minDate={
          parseDateOnlyValue(minDate)
          ?? undefined
        }
        maxDate={
          parseDateOnlyValue(maxDate)
          ?? undefined
        }
        onChange={(selectedDate) => {
          if (
            !selectedDate
            || Number.isNaN(
              selectedDate.getTime(),
            )
          ) {
            onChange('');
            return;
          }

          onChange(
            format(
              selectedDate,
              'yyyy-MM-dd',
            ),
          );
        }}
        format="dd/MM/yyyy"
        slotProps={{
          textField: {
            size,
            fullWidth: true,
            required,
            helperText,
            sx: {
              '& .MuiInputBase-root': {
                color: '#f8fafc',
                bgcolor:
                  'rgba(15,23,42,0.44)',
              },
              '& .MuiInputLabel-root': {
                color: '#94a3b8',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor:
                  'rgba(148,163,184,0.28)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor:
                  'rgba(56,189,248,0.46)',
              },
              '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#38bdf8',
              },
              '& .MuiSvgIcon-root': {
                color: '#7dd3fc',
              },
            },
          },
          actionBar: {
            actions: [
              'clear',
              'today',
            ],
          },
        }}
      />
    </LocalizationProvider>
  );
}
